import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { ThemeModeContext } from '../theme/ThemeModeProvider.jsx';
import { LinearProgress } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import './TopBar.css';
import { useTopBar } from './TopBarContext';
import HelpModal from './Help/HelpModal';


const TopBar = ({ userDropdown, onMenuToggle, sidebarCollapsed }) => {
  const location = useLocation();
  const { centerContent } = useTopBar();
  const [, setRerenderTick] = useState(0);
  const [helpModalOpen, setHelpModalOpen] = useState(false);


  // Live-update badges for Leave Approvals summary
  useEffect(()=>{
    const handler = ()=> setRerenderTick(v=>v+1);
    window.addEventListener('hrapp.leave.approvals.summary', handler);
    return ()=> window.removeEventListener('hrapp.leave.approvals.summary', handler);
  },[]);

  // Re-render on global loading changes
  useEffect(()=>{
    const rerender = ()=> setRerenderTick(v=>v+1);
    window.addEventListener('hrapp.loading.change', rerender);
    return ()=> window.removeEventListener('hrapp.loading.change', rerender);
  },[]);


  const getBreadcrumbs = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    const breadcrumbMap = {
      '': 'Dashboard',
      'employees': 'Employees',
      'contracts': 'Contracts',
      'offers': 'Offers',
      'permissions-test': 'Permissions',
      'profile': 'Profile',
      'cv-upload': 'CV Upload',
      'contract-processing': 'Contract Processing',
      'admin': 'Admin',
      'employee-case': 'Employee File'
    };

    if (segments.length === 0) {
      return [{ label: 'Dashboard', path: '/' }];
    }

    const breadcrumbs = [{ label: 'Dashboard', path: '/' }];

    segments.forEach((segment, index) => {
      const path = '/' + segments.slice(0, index + 1).join('/');
      const label = breadcrumbMap[segment] || segment;
      breadcrumbs.push({ label, path });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <>
      {/* Global thin progress bar (shows when window.__loading > 0) */}
      {window.__loadingCount > 0 && (
        <LinearProgress sx={{ position:'fixed', top:0, left:0, right:0, height:2, zIndex:13000 }} />
      )}

      <Box className="topbar">
      {/* Left Section */}
      <Box className="topbar-left">
        {/* Hamburger Menu Button */}
        <IconButton
          className="hamburger-btn"
          onClick={onMenuToggle}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <MenuIcon />
        </IconButton>

        {/* App Title & Breadcrumbs */}
        <Box className="topbar-nav">
          <Box className="topbar-logo">
            <img
              src="/images/inspirit-logo.png"
              alt="Inspirit Logo"
              className="topbar-logo-image"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Typography variant="h6" className="app-title-fallback" style={{ display: 'none' }}>
              INSPIRIT
            </Typography>
          </Box>

          <Box className="breadcrumb-section">
            <Breadcrumbs
              separator="›"
              className="breadcrumbs"
              maxItems={3}
            >
              {breadcrumbs.map((crumb, index) => (
                index === breadcrumbs.length - 1 ? (
                  <Typography key={crumb.path} className="breadcrumb-current">
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.path}
                    component={RouterLink}
                    to={crumb.path}
                    className="breadcrumb-link"
                  >
                    {crumb.label}
                  </Link>
                )
              ))}
            </Breadcrumbs>

            {/* Status Indicators */}
            <Box className="status-indicators">
              <Chip
                label="Live"
                size="small"
                className="status-chip live"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Center Section - Contextual content (optional) */}
      <Box className="topbar-center">
        {centerContent ? (
          centerContent
        ) : location.pathname==='/leave' ? (
          <Typography variant="h6" sx={{ fontWeight: 400, ml:1, whiteSpace:'nowrap' }}>My Leave Requests</Typography>
        ) : location.pathname==='/leave-approvals' ? (
          <Typography variant="h6" sx={{ fontWeight: 400, ml:1, whiteSpace:'nowrap' }}>Leave Approvals</Typography>
        ) : null}
      </Box>

      {/* Right Section */}
      <Box className="topbar-right">
        {location.pathname==='\u002Fleave' ? (
          <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
            <Button variant="contained" size="small" sx={{ whiteSpace:'nowrap' }} onClick={()=>window.dispatchEvent(new CustomEvent('hrapp.open.new.request'))}>ADD REQUEST</Button>
            <Button variant="outlined" size="small" sx={{ whiteSpace:'nowrap' }} onClick={()=>window.dispatchEvent(new CustomEvent('hrapp.leave.refresh'))}>REFRESH FORM</Button>
            <IconButton
              size="small"
              onClick={() => setHelpModalOpen(true)}
              title="User Guide"
              sx={{ color: 'primary.main' }}
            >
              <HelpIcon />
            </IconButton>
            <span className="subtopbar-chip" title="Available" style={{ marginLeft:8 }}>Available: <b>{window.__leaveSummary?.available ?? '-'}</b></span>
            <span className="subtopbar-chip" title="Projected">Projected: <b>{window.__leaveSummary?.projected ?? '-'}</b></span>
          </Box>
        ) : location.pathname==='\u002Fleave-approvals' ? (
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <span className="subtopbar-chip" title="Pending">Pending: <b>{window.__leaveApprovalsSummary?.pending ?? '-'}</b></span>
            <span className="subtopbar-chip" title="Cancel Requests">Cancel: <b>{window.__leaveApprovalsSummary?.cancel ?? '-'}</b></span>
          </Box>
        ) : null}

        {/* Theme mode toggle */}
        <ThemeModeContext.Consumer>
          {({ mode, toggle }) => (
            <IconButton className="action-icon" aria-label={mode==='light'? 'Switch to dark mode' : 'Switch to light mode'} title={mode==='light'? 'Dark mode' : 'Light mode'} onClick={toggle}>
              <span className="material-symbols-outlined" style={{ fontSize:20 }}>{mode==='light'?'dark_mode':'light_mode'}</span>
            </IconButton>
          )}
        </ThemeModeContext.Consumer>




        {/* User Profile */}
        <Box className="user-section">
          {userDropdown}
        </Box>
      </Box>
    </Box>

    {/* Help Modal */}
    <HelpModal
      open={helpModalOpen}
      onClose={() => setHelpModalOpen(false)}
      helpType="leave-guide"
    />
    </>
  );
};

export default TopBar;
